import { Package, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { EmptyState } from '../../../shared/components/EmptyState';
import { SkeletonCard } from '../../../shared/components/Skeleton';
import { formatBRL } from '../../../shared/lib/format';
import {
  useCategorias,
  useCreateProduto,
  useDeleteProduto,
  useGrupos,
  useProdutos,
  useUpdateProduto,
} from '../hooks';
import type { Produto } from '../types';
import { ProdutoFormModal } from './ProdutoFormModal';

export function ProdutosTab() {
  const [q, setQ] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState<string>('');

  const { data: produtos, isLoading } = useProdutos({
    q: q.trim() || undefined,
    categoria_id: categoriaFiltro || undefined,
  });
  const { data: categorias } = useCategorias();
  const { data: grupos } = useGrupos(true);

  const createMut = useCreateProduto();
  const updateMut = useUpdateProduto();
  const deleteMut = useDeleteProduto();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Produto | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Produto | null>(null);

  const cats = useMemo(() => categorias ?? [], [categorias]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (p: Produto) => {
    setEditing(p);
    setFormOpen(true);
  };

  const getPrecoDisplay = (p: Produto) => {
    if (p.tipo === 'peso') return `${formatBRL(p.preco_por_kg ?? 0)}/kg`;
    if (p.tamanhos.length > 0) {
      const min = Math.min(...p.tamanhos.map((t) => t.preco));
      return `a partir de ${formatBRL(min)}`;
    }
    return formatBRL(p.preco_unitario ?? 0);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              className="input pl-9"
              placeholder="Buscar por nome…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <select
            className="input sm:w-48"
            value={categoriaFiltro}
            onChange={(e) => setCategoriaFiltro(e.target.value)}
          >
            <option value="">Todas as categorias</option>
            {cats.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
        </div>

        <button
          className="btn-primary"
          onClick={openCreate}
          disabled={cats.length === 0}
          title={cats.length === 0 ? 'Crie pelo menos uma categoria antes' : undefined}
        >
          <Plus className="h-4 w-4" /> Novo produto
        </button>
      </div>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : (produtos ?? []).length === 0 ? (
        <EmptyState
          icon={Package}
          title="Nenhum produto encontrado"
          description={
            cats.length === 0
              ? 'Crie uma categoria na aba Categorias para cadastrar produtos.'
              : 'Cadastre o primeiro produto do seu PDV.'
          }
          action={
            cats.length > 0 && (
              <button className="btn-primary" onClick={openCreate}>
                <Plus className="h-4 w-4" /> Novo produto
              </button>
            )
          }
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {produtos!.map((p) => (
            <div key={p.id} className="card flex flex-col p-4">
              <div className="mb-2 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-medium text-slate-900">{p.nome}</p>
                  <div className="mt-0.5 flex items-center gap-2">
                    {p.categoria && (
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{ backgroundColor: p.categoria.cor }}
                      />
                    )}
                    <span className="text-xs text-slate-500">
                      {p.categoria?.nome ?? '—'} •{' '}
                      {p.tipo === 'peso' ? 'Por peso' : 'Unitario'}
                      {!p.ativo && ' • Inativo'}
                    </span>
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => openEdit(p)}
                    className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                    aria-label="Editar"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(p)}
                    className="rounded-lg p-2 text-red-500 hover:bg-red-50"
                    aria-label="Excluir"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <p className="mt-auto text-base font-semibold text-brand-700">
                {getPrecoDisplay(p)}
              </p>

              {p.descricao && (
                <p className="mt-1 line-clamp-2 text-xs text-slate-500">{p.descricao}</p>
              )}

              {(p.tamanhos.length > 0 || p.grupos.length > 0) && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {p.tamanhos.slice(0, 4).map((t) => (
                    <span
                      key={t.id}
                      className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700"
                    >
                      {t.nome}
                    </span>
                  ))}
                  {p.grupos.slice(0, 3).map((g) => (
                    <span
                      key={g.id}
                      className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] text-brand-700"
                    >
                      {g.nome}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ProdutoFormModal
        open={formOpen}
        produto={editing}
        categorias={cats}
        grupos={grupos ?? []}
        onClose={() => setFormOpen(false)}
        loading={createMut.isPending || updateMut.isPending}
        onSubmit={async (input) => {
          if (editing) {
            await updateMut.mutateAsync({ id: editing.id, body: input });
          } else {
            await createMut.mutateAsync(input);
          }
          setFormOpen(false);
        }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        title="Excluir produto"
        message={`Deseja realmente excluir "${confirmDelete?.nome}"?`}
        variant="danger"
        loading={deleteMut.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={async () => {
          if (confirmDelete) {
            await deleteMut.mutateAsync(confirmDelete.id);
            setConfirmDelete(null);
          }
        }}
      />
    </div>
  );
}
